import { ReportField, ReportFieldValue, ReportFormValues, ReportGeneration, ReportSchema } from '../../types';

// Rules here follow field_type_reference.md (report_generation_poc).

// A slider must have validation.min/max per the reference; 0–100 only keeps a
// badly authored field usable instead of broken.
export const sliderRange = (field: ReportField): { min: number; max: number } => ({
  min: field.validation?.min ?? 0,
  max: field.validation?.max ?? 100,
});

const initialValueForField = (field: ReportField): ReportFieldValue => {
  if (field.default_value !== undefined) return field.default_value;
  if (field.type === 'multi_choice') return [];
  // A slider can't show "empty", so it starts at its min.
  if (field.type === 'number' && field.display_style === 'slider') return sliderRange(field).min;
  return null;
};

export const buildInitialValues = (fields: ReportField[]): ReportFormValues => {
  const values: ReportFormValues = {};
  for (const field of fields) {
    values[field.field_key] = initialValueForField(field);
  }
  return values;
};

const isEmpty = (value: ReportFieldValue): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

export const validateFields = (fields: ReportField[], values: ReportFormValues): Record<string, string> => {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    const value = values[field.field_key];

    if (isEmpty(value)) {
      if (field.required) errors[field.field_key] = 'This field is required.';
      continue;
    }

    if (field.type === 'text' && typeof value === 'string') {
      const { max_length, pattern } = field.validation ?? {};
      if (max_length !== undefined && value.length > max_length) {
        errors[field.field_key] = `Must be at most ${max_length} characters.`;
      } else if (pattern) {
        try {
          if (!new RegExp(pattern).test(value)) errors[field.field_key] = 'Invalid format.';
        } catch (e) {
          // Malformed pattern in the report config — don't block submit over it.
          console.warn(`[report-form] Invalid validation.pattern on field "${field.field_key}":`, e);
        }
      }
    }

    if (field.type === 'number' && typeof value === 'number') {
      const { min, max } = field.validation ?? {};
      if (min !== undefined && value < min) {
        errors[field.field_key] = `Must be at least ${min}.`;
      } else if (max !== undefined && value > max) {
        errors[field.field_key] = `Must be at most ${max}.`;
      }
    }
  }

  return errors;
};

// The `report_generation` object on the chat message. Every field_key is
// sent; an unanswered optional field is null ([] for multi_choice).
export const buildReportGeneration = (schema: ReportSchema, values: ReportFormValues): ReportGeneration => {
  const payloadValues: ReportFormValues = {};
  for (const field of schema.fields) {
    const value = values[field.field_key];
    payloadValues[field.field_key] = isEmpty(value) ? (field.type === 'multi_choice' ? [] : null) : value;
  }
  return { report_id: schema.report_id, values: payloadValues };
};

// One answer as the user would read it: option labels instead of values, Yes/No for booleans.
const answerText = (field: ReportField, value: ReportFieldValue): string => {
  const labelOf = (v: string | number | boolean) => field.options?.find((o) => o.value === v)?.label ?? String(v);
  if (Array.isArray(value)) return value.map(labelOf).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return labelOf(value as string | number);
};

// The chat message the user sees (and the backend gets as `query`): the report
// name, then "Label: answer" for each answered field.
export const buildReportSummary = (reportName: string, schema: ReportSchema, values: ReportFormValues): string =>
  [
    reportName,
    ...schema.fields
      .filter((field) => !isEmpty(values[field.field_key]))
      .map((field) => `${field.field_label}: ${answerText(field, values[field.field_key])}`),
  ].join('\n');
